import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ACTIVITY_STATUSES, stuckDays, type ActivityStatus, type ActivityType, type WorkSprint } from "@torre/work-hours";
import Modal from "../../../components/base/Modal";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { canShowStaffCosts } from "../../../config/platformFeatures";
import ActividadFormModal from "./ActividadFormModal";
import { activityErrorMessage, archiveActivity, updateActivityStatus, type ActivityRow } from "../services/activitiesService";
import { STATUS_TONE, formatMinutes, formatMoney, githubUrl, shortDate, typeOf } from "../lib/format";

// ---------------------------------------------------------------------------
// Detalle de un ticket: todo lo que se ve en la tarjeta más descripción, GitHub,
// horas registradas contra estimación, costo (dueños) y acciones: cambiar
// estado, editar y archivar (dueños). Nada se borra.
// ---------------------------------------------------------------------------

interface Props {
  activity: ActivityRow;
  catalog: readonly ActivityType[];
  sprints: readonly WorkSprint[];
  epics: readonly ActivityRow[];
  isOwner: boolean;
  canManage: boolean;
  onClose: () => void;
  /** `null` ⇒ se archivó. */
  onChanged: (activity: ActivityRow | null) => void;
}

export default function ActividadDetailModal({ activity: initial, catalog, sprints, epics, isOwner, canManage, onClose, onChanged }: Props) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const [a, setA] = useState<ActivityRow>(initial);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const type = typeOf(catalog, a.typeKey);
  const gh = githubUrl(a);
  const parent = a.parentId ? epics.find((e) => e.id === a.parentId) : null;
  const sprint = a.sprintId ? sprints.find((s) => s.id === a.sprintId) : null;
  // Sin costos donde Nómina/Staff están apagadas (WAYAK): regla de nómina.
  const cost = isOwner && a.cost && canShowStaffCosts() ? a.cost : null;
  const stuck = stuckDays(a, new Date().toISOString());

  const changeStatus = async (status: ActivityStatus) => {
    if (status === a.status) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await updateActivityStatus(a.id, status);
      const next = saved ?? { ...a, status, statusChangedAt: new Date().toISOString() };
      setA(next);
      onChanged(next);
    } catch (err) {
      setError(activityErrorMessage(err) ?? t("staffActivities.kanban.statusError"));
    } finally {
      setBusy(false);
    }
  };

  const archive = async () => {
    if (!window.confirm(t("staffActivities.detail.archiveConfirm"))) return;
    setBusy(true);
    try {
      await archiveActivity(a.id);
      onChanged(null);
      onClose();
    } catch (err) {
      setError(activityErrorMessage(err) ?? t("staffActivities.common.error"));
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <ActividadFormModal
        mode="edit"
        activity={a}
        catalog={catalog}
        sprints={sprints}
        epics={epics}
        onClose={() => setEditing(false)}
        onSaved={(saved) => {
          const next = { ...a, ...saved };
          setA(next);
          onChanged(next);
          setEditing(false);
        }}
      />
    );
  }

  const row = (label: string, value: React.ReactNode) => (
    <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
      <span className={tc.textSecondary}>{label}</span>
      <span className={`text-right ${tc.text}`}>{value}</span>
    </div>
  );

  return (
    <Modal
      title={`${a.key} · ${type.name}`}
      themeClasses={tc}
      onClose={onClose}
      width="max-w-2xl"
      buttonAction={
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setEditing(true)} className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-500" data-testid="activity-edit">
            <i className="ri-edit-line mr-1" />
            {t("staffActivities.detail.edit")}
          </button>
          {canManage && !a.archived && (
            <button type="button" onClick={() => void archive()} disabled={busy} className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-500/10" data-testid="activity-archive">
              <i className="ri-archive-line mr-1" />
              {t("staffActivities.detail.archive")}
            </button>
          )}
        </div>
      }
      content={
        <div className="space-y-4" data-testid="activity-detail">
          <div>
            <h3 className={`text-lg font-semibold ${tc.text}`}>{a.title}</h3>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              <span className={`rounded-full px-2 py-0.5 font-medium ${STATUS_TONE[a.status]}`}>{t(`staffActivities.status.${a.status}`)}</span>
              <span className={`rounded-full bg-gray-400/15 px-2 py-0.5 ${tc.textSecondary}`}>{t(`staffActivities.source.${a.source}`, { defaultValue: a.source })}</span>
              <span className={`rounded-full bg-gray-400/15 px-2 py-0.5 ${tc.textSecondary}`}>{t(`staffActivities.priority.${a.priority}`)}</span>
              {a.archived && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-red-500">{t("staffActivities.detail.archived")}</span>}
              {stuck != null && stuck > 0 && (
                <span className={tc.textSecondary}>
                  <i className="ri-pause-circle-line mr-1" />
                  {t("staffActivities.detail.sinceStatus", { date: a.statusChangedAt ? new Date(a.statusChangedAt).toLocaleDateString("es-MX") : "—" })}
                </span>
              )}
            </div>
          </div>

          <div>
            <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>{t("staffActivities.detail.changeStatus")}</label>
            <select value={a.status} disabled={busy} onChange={(e) => void changeStatus(e.target.value as ActivityStatus)} className={`rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`} aria-label={t("staffActivities.detail.changeStatus")} data-testid="activity-status-select">
              {ACTIVITY_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`staffActivities.status.${s}`)}
                </option>
              ))}
            </select>
          </div>

          <div className={`rounded-xl border p-3 ${tc.cardBorder}`}>
            <p className={`whitespace-pre-wrap text-sm ${a.description ? tc.text : `italic ${tc.textSecondary}`}`}>{a.description || t("staffActivities.detail.noDescription")}</p>
          </div>

          <div className={`divide-y rounded-xl border px-3 ${tc.cardBorder} divide-gray-200 dark:divide-dark-border`}>
            {row(t("staffActivities.form.assignee"), a.assigneeName ?? t("staffActivities.form.noAssignee"))}
            {row(t("staffActivities.form.sprint"), sprint ? `${sprint.name} (${sprint.startDate} → ${sprint.endDate})` : t("staffActivities.form.noSprint"))}
            {row(t("staffActivities.form.points"), a.storyPoints ?? "—")}
            {row(
              t("staffActivities.detail.hours"),
              a.estimateMinutes != null
                ? t("staffActivities.detail.loggedVsEstimate", { logged: formatMinutes(a.loggedMinutes), estimate: formatMinutes(a.estimateMinutes) })
                : t("staffActivities.detail.loggedOnly", { logged: formatMinutes(a.loggedMinutes) }),
            )}
            {row(t("staffActivities.form.startDate"), a.startDate ? shortDate(a.startDate) : "—")}
            {row(t("staffActivities.form.dueDate"), a.dueDate ? shortDate(a.dueDate) : "—")}
            {row(t("staffActivities.form.parent"), parent ? `${parent.key} · ${parent.title}` : t("staffActivities.form.noParent"))}
            {a.projectId && row("Proyecto", a.projectId)}
            {a.repo && row("Repo", <span className="font-mono text-xs">{a.repo}</span>)}
            {gh &&
              row(
                t("staffActivities.detail.github"),
                <a href={gh} target="_blank" rel="noopener noreferrer" className="text-violet-500 hover:underline">
                  <i className="ri-github-fill mr-1" />
                  {a.key}
                  {a.github?.state ? ` · ${a.github.state}` : ""}
                </a>,
              )}
            {a.crm && row(t("staffActivities.detail.crm"), [a.crm.name, a.crm.stage].filter(Boolean).join(" · ") || "—")}
            {cost &&
              row(
                t("staffActivities.card.cost"),
                <span className="text-emerald-600" data-testid={`activity-cost-${a.id}`}>
                  {t("staffActivities.card.costEstimated")}: {formatMoney(cost.estimated, cost.currency)} · {t("staffActivities.card.costActual")}: {formatMoney(cost.actual, cost.currency)}
                  <span className={`block text-[11px] ${tc.textSecondary}`}>{formatMoney(cost.hourlyCost, cost.currency)}/h</span>
                </span>,
              )}
            {row(t("staffActivities.detail.created"), a.createdAt ? new Date(a.createdAt).toLocaleString("es-MX") : "—")}
          </div>

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-500">
              <i className="ri-error-warning-line mr-1" />
              {error}
            </div>
          )}
        </div>
      }
    />
  );
}
